/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { FunctionComponent, VNode } from 'preact';
import { HTMLAttributes } from 'preact/compat';
import { classes, VComponent } from '@adobe-commerce/elsie/lib';
import {
  Divider,
  Skeleton,
  SkeletonRow,
} from '@adobe-commerce/elsie/components';
import '@/quote-management/components/QuoteSummaryList/QuoteSummaryList.css';

export interface QuoteSummaryListProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'loading'> {
  heading?: VNode | null;
  footer?: VNode | null;
  products?: VNode | null;
  outOfStockMessage?: VNode | null;
  loading?: boolean;
  variant?: 'primary' | 'secondary';
}

export const QuoteSummaryList: FunctionComponent<QuoteSummaryListProps> = ({
  className,
  children,
  heading,
  footer,
  products,
  outOfStockMessage,
  loading = true,
  variant = 'primary',
  ...props
}) => {
  return (
    <div
      {...props}
      className={classes([
        'quote-management-quote-summary-list',
        className,
        `quote-management-quote-summary-list__background--${variant}`,
      ])}
    >
      {loading ? (
        <QuoteSummaryListSkeleton />
      ) : (
        <>
          {(heading || outOfStockMessage) && (
            <div
              data-testid="quote-summary-list-heading-wrapper"
              className={classes([
                'quote-management-quote-summary-list__heading',
                [
                  'quote-management-quote-summary-list__heading--full-width',
                  !products,
                ],
              ])}
            >
              {heading && (
                <>
                  <VComponent
                    node={heading}
                    className="quote-management-quote-summary-list__heading-text"
                  />
                  <Divider
                    variant="primary"
                    className={classes([
                      'quote-management-quote-summary-list__heading-divider',
                    ])}
                  />
                </>
              )}

              {outOfStockMessage && (
                <VComponent
                  node={outOfStockMessage}
                  className="quote-management-quote-summary-list__out-of-stock-message"
                />
              )}
            </div>
          )}
          <div
            className={classes([
              'quote-management-quote-summary-list__content',
            ])}
          >
            {products}
          </div>

          {footer && (
            <>
              <Divider
                variant="primary"
                className={classes([
                  'quote-management-quote-summary-list__footer-divider',
                ])}
              />
              <VComponent
                node={footer}
                className="quote-management-quote-summary-list__footer-text"
              />
            </>
          )}
        </>
      )}
    </div>
  );
};

const QuoteSummaryListSkeleton: FunctionComponent = () => {
  return (
    <Skeleton
      data-testid="quote-summary-list-skeleton"
      className="quote-management-quote-summary-list__skeleton"
      rowGap="medium"
    >
      <SkeletonRow
        variant="row"
        size="xlarge"
        fullWidth={true}
        lines={3}
        multilineGap="small"
      />
    </Skeleton>
  );
};
